import classNames from "classnames";
import { Warning } from "neetoicons";
import { Tooltip, Typography, Spinner } from "neetoui";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import DateRangeFilter from "components/DateRangeFilter";

import { DEFAULT_DEVICES, DEFAULT_BROWSERS, DEFAULT_OS } from "./constants";
import MetaInfo from "./MetaInfo";
import MetricItem from "./MetricItem";

const Insights = ({
  insightsData,
  isLoading,
  timePeriod,
  setTimePeriod,
  helpTexts = {},
  showWarningMessage = false,
  className,
  title,
  tooltipProps = {},
}) => {
  const { t } = useTranslation();

  const {
    visitCount,
    startCount,
    submissionCount,
    completionRate,
    averageSubmissionTime,
    devices,
    operatingSystems,
    browsers,
  } = insightsData;

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div
      className={classNames("mx-auto pt-10", className)}
      data-testid="insights"
    >
      <div className="mb-10 flex w-full items-center">
        <Typography data-testid="submissions-insights-title" style="h2">
          {title || t("neetoMolecules.insights.title")}
        </Typography>
        {showWarningMessage && (
          <Tooltip
            content={t("neetoMolecules.insights.dataInaccuracyWarning")}
            position="bottom"
            touch={["hold", 500]}
            {...tooltipProps}
          >
            <span className="ms-3">
              <Warning
                className="neeto-ui-text-warning-500"
                data-testid="insights-warning"
                size={20}
              />
            </span>
          </Tooltip>
        )}
        <span className="ms-auto">
          <DateRangeFilter {...{ setTimePeriod, timePeriod }} />
        </span>
      </div>
      <div className="mb-6 grid grid-cols-1 gap-x-9 gap-y-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 lg:gap-0">
        <MetricItem
          helpText={helpTexts["visitCount"]}
          metric={t("neetoMolecules.insights.metrics.visits")}
          value={visitCount}
        />
        <MetricItem
          helpText={helpTexts["startCount"]}
          metric={t("neetoMolecules.insights.metrics.starts")}
          value={startCount}
        />
        <MetricItem
          helpText={helpTexts["submissions"]}
          metric={t("neetoMolecules.insights.metrics.submissions")}
          value={submissionCount}
        />
        <MetricItem
          isPercentage
          helpText={helpTexts["completionRate"]}
          metric={t("neetoMolecules.insights.metrics.completionRate")}
          value={completionRate}
        />
        <MetricItem
          isTime
          helpText={helpTexts["averageSubmissionTime"]}
          metric={t("neetoMolecules.insights.metrics.avgTimeToComplete")}
          timeSuffix={t("neetoMolecules.insights.mins")}
          value={averageSubmissionTime}
        />
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <MetaInfo
          data={devices}
          defaultItems={DEFAULT_DEVICES}
          type={t("neetoMolecules.insights.devices")}
        />
        <MetaInfo
          data={operatingSystems}
          defaultItems={DEFAULT_OS}
          type={t("neetoMolecules.insights.os")}
        />
        <MetaInfo
          data={browsers}
          defaultItems={DEFAULT_BROWSERS}
          type={t("neetoMolecules.insights.browsers")}
        />
      </div>
    </div>
  );
};

Insights.propTypes = {
  /**
   * Title displayed for the Insights component.
   */
  title: PropTypes.string,
  /**
   * Props for the tooltip displayed on the warning icon.
   */
  tooltipProps: PropTypes.object,
  /**
   * Object containing metrics and statistics for insights
   */
  insightsData: PropTypes.shape({
    visitCount: PropTypes.number,
    startCount: PropTypes.number,
    submissionCount: PropTypes.number,
    completionRate: PropTypes.number,
    averageSubmissionTime: PropTypes.shape({
      h: PropTypes.number,
      m: PropTypes.number,
      s: PropTypes.number,
    }),
    devices: PropTypes.shape({
      desktop: PropTypes.number,
      smartphone: PropTypes.number,
    }),
    operatingSystems: PropTypes.shape({
      mac: PropTypes.number,
      windows: PropTypes.number,
    }),
    browsers: PropTypes.shape({
      chrome: PropTypes.number,
      safari: PropTypes.number,
    }),
  }),
  /**
   * Indicates whether insights data is currently being fetched from an API
   */
  isLoading: PropTypes.bool,
  /**
   * The state corresponding to the currently selected date range.
   */
  timePeriod: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.object,
  }),
  /**
   * The setter method for `timePeriod` state.
   */
  setTimePeriod: PropTypes.func,
  /**
   * Object containing explanatory text for each metric, providing additional context that can help users understand the significance of the metrics displayed.
   */
  helpTexts: PropTypes.shape({
    visitCount: PropTypes.string,
    startCount: PropTypes.string,
    submissions: PropTypes.string,
    completionRate: PropTypes.string,
    averageSubmissionTime: PropTypes.string,
  }),
  /**
   * Displays a warning to indicate that the insights may not be accurate, as the insights integration occurred after the record's creation.
   */
  showWarningMessage: PropTypes.bool,
  /**
   * To provide additional classes.
   */
  className: PropTypes.string,
};

export default Insights;
